import type { RefObject } from 'react';
import { DEFAULT_PRODUCTION_BRANCH } from '@hangar/shared';
import { SLUG_HINT } from '@/lib/format';
import { FormField, fieldA11y } from '@/components/FormField';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import type { useNewProjectForm } from './useNewProjectForm';

/** The state `useNewProjectForm` returns; every field reads its value, message and handler from it. */
export type NewProjectState = ReturnType<typeof useNewProjectForm>;

export function NameField({ f }: { f: NewProjectState }) {
  return (
    <FormField id="project-name" label="Name" required error={f.errors.name}>
      <Input ref={f.nameRef} data-testid="project-name" value={f.values.name} placeholder="My product" autoComplete="off" maxLength={120} onChange={(e) => f.change('name', e.target.value)} {...fieldA11y('project-name', f.errors.name)} />
    </FormField>
  );
}

export function SlugField({ f, slugRef }: { f: NewProjectState; slugRef?: RefObject<HTMLInputElement | null> }) {
  return (
    <FormField id="project-slug" label="Slug" required hint={SLUG_HINT} error={f.errors.slug}>
      <Input
        ref={slugRef}
        data-testid="project-slug"
        className="font-mono sm:text-[13px]"
        value={f.values.slug}
        placeholder="my-product"
        autoComplete="off"
        spellCheck={false}
        maxLength={63}
        onChange={(e) => f.change('slug', e.target.value)}
        {...fieldA11y('project-slug', f.errors.slug, SLUG_HINT)}
      />
    </FormField>
  );
}

export function DescriptionField({ f }: { f: NewProjectState }) {
  return (
    <FormField id="project-description" label="Description" error={f.errors.description} className="sm:col-span-2">
      <Textarea
        data-testid="project-description"
        value={f.values.description}
        placeholder="What ships from this project"
        maxLength={2000}
        onChange={(e) => f.change('description', e.target.value)}
        {...fieldA11y('project-description', f.errors.description)}
      />
    </FormField>
  );
}

export function DefaultEnvironmentsOption({ f }: { f: NewProjectState }) {
  return (
    <div className="flex min-h-9 items-center gap-2.5 max-sm:min-h-11">
      <Checkbox id="project-default-envs" checked={f.values.defaults} onCheckedChange={(v) => f.setDefaults(v === true)} />
      <Label htmlFor="project-default-envs" className="cursor-pointer font-normal leading-snug text-foreground">
        Create staging, preprod and production environments (production protected)
      </Label>
    </div>
  );
}

/** Only with the default environments: the branch production builds from. */
export function ProductionBranchField({ f }: { f: NewProjectState }) {
  if (!f.values.defaults) return null;
  const error = f.errors.productionBranch;
  return (
    <FormField
      id="project-production-branch"
      label="Production branch"
      hint={`The branch production builds from; preprod takes it and release/*. Empty: ${DEFAULT_PRODUCTION_BRANCH}. Editable later in Project settings.`}
      error={error}
      className="sm:max-w-sm"
    >
      <Input
        data-testid="project-production-branch"
        className="font-mono sm:text-[13px]"
        value={f.values.branch}
        placeholder={DEFAULT_PRODUCTION_BRANCH}
        autoComplete="off"
        spellCheck={false}
        maxLength={100}
        onChange={(e) => f.change('productionBranch', e.target.value)}
        {...fieldA11y('project-production-branch', error, 'project-production-branch-hint')}
      />
    </FormField>
  );
}
